import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Header from '../components/Header';
import '../index.css';
import '../customTable.css'; // Import the custom CSS for the new table
import CustomTable, { Column } from '../components/CustomTable'; // Import the new CustomTable
import ProductModal from '../components/ProductModal';
import { ProductInterface } from '../types';
import moment from 'moment';
import apiClient from '../services/apiClient';
import EmptyState from '../components/EmptyState';
import ProductIcon from '../assets/svgs/ProductIcon';
import ReactGA from 'react-ga4';
import { CreateButton } from '../components/SharedComponents';
import { getCurrency } from '../services/tableHelper';
import { useSelector } from 'react-redux';
import Tabbed from '../components/Tabbed';
import ProductCategories, { ProductCategoriesRef } from '../components/ProductCategories';
import CategoryIcon from '@mui/icons-material/Category';

const Wrapper = styled.div`
	background-color: white;
	width: 100%;
	min-height: 100vh;
	padding: 30px 40px;
	box-sizing: border-box;
	@media (max-width: 768px) {
		padding: 20px 20px;
	}
`;

export default function Products() {
	const [isEditPanelVisible, setIsEditPanelVisible] = useState<boolean>(false);
	const [selectedProduct, setSelectedProduct] = useState<ProductInterface | null>(null);
	const [products, setProducts] = useState<ProductInterface[]>([]);
	const [fetchState, setFetchState] = useState<'loading' | 'success' | 'failed'>('loading');
	const [activeTab, setActiveTab] = useState<number>(0);
	const categoriesRef = React.useRef<ProductCategoriesRef>(null);

	const currency = useSelector((state: any) => getCurrency(state.auth.user.currency));

	const columns: Column[] = [
		{
			field: 'product_images',
			headerName: 'Image',
			minWidth: 80,
			headerClassName: 'table-header',
			cellClassName: 'text-center',
			isNotSearchable: true,
			renderCell: (params) => {
				let imageUrl;

				if (params.value && Array.isArray(params.value) && params.value.length > 0) {
					imageUrl = params.value[0];
				}

				// If no valid image URL, show placeholder
				return imageUrl ? (
					<img
						src={imageUrl}
						alt={`${params.row.name} thumbnail`}
						className="w-10 h-10 rounded-md object-cover"
					/>
				) : (
					<div className="w-10 h-10 rounded-md bg-gray-100 flex items-center justify-center">
						<ProductIcon width={24} height={24} />
					</div>
				);
			},
			flex: 0.5,
		},
		{
			field: 'name',
			headerName: 'Name',
			minWidth: 260,
			headerClassName: 'table-header',
			cellClassName: 'font-medium text-gray-900',
			flex: 2,
		},
		{
			field: 'category_name',
			headerName: 'Category',
			minWidth: 150,
			headerClassName: 'table-header',
			cellClassName: 'text-gray-600',
			renderCell: (params) => {
				return params.value ? (
					<span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-xs">{params.value}</span>
				) : (
					<span className="text-gray-400 text-xs">No Category</span>
				);
			},
			flex: 1,
		},
		{
			field: 'default_price',
			headerName: 'Price',
			minWidth: 140,
			headerClassName: 'table-header',
			cellClassName: 'text-gray-500',
			valueGetter: (params) => `${currency} ${Number(params.value).toFixed(2)}`,
			flex: 1,
		},
		{
			field: 'is_visible_in_storefront',
			headerName: 'Storefront',
			minWidth: 120,
			headerClassName: 'table-header',
			renderCell: (params) => {
				// Default to true if not specified
				const isVisible = params.value !== false;
				return (
					<div
						className={`px-2 py-1 rounded-md text-xs ${
							isVisible ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
						}`}
						style={{ display: 'inline' }}
					>
						{isVisible ? 'Visible' : 'Hidden'}
					</div>
				);
			},
			flex: 0.8,
		},
		{
			field: 'date_updated',
			headerName: 'Last Updated',
			minWidth: 150,
			headerClassName: 'table-header',
			cellClassName: 'text-gray-500',
			renderCell: (params) => moment(params.value).format('Do MMM YYYY'),
			flex: 1,
		},
	];

	useEffect(() => {
		console.log('Products component mounted or products changed:', products);
	}, [products]);

	useEffect(() => {
		ReactGA.send({
			hitType: 'pageview',
			page: '/products',
			title: 'Products',
		});
		setFetchState('loading');
		apiClient
			.get('/products')
			.then((response: any) => {
				// Process products to ensure product_images are parsed correctly
				const processedProducts = response.data.map((product: any) => {
					// console.log(product.product_images, 'product_images before processing');
					// Handle different cases for product_images
					if (product.product_images === null || product.product_images === undefined) {
						// Keep it as null - this is the desired state for products with no images
						product.product_images = [];
					}
					// If it's a string (like "[]"), parse it
					else if (typeof product.product_images === 'string') {
						try {
							// If it's an empty array as string, set to null
							if (product.product_images === '[]' || product.product_images === '') {
								product.product_images = [];
							} else {
								// Otherwise parse it as JSON
								product.product_images = JSON.parse(product.product_images);
								// console.log(product.product_images, 'product_images after parsing string');
							}
						} catch (e) {
							console.error('Error parsing product images:', e);
							product.product_images = null;
						}
					}
					// If it's an empty array, set to null
					else if (Array.isArray(product.product_images) && product.product_images.length === 0) {
						product.product_images = [];
					}

					// Ensure is_visible_in_storefront is a proper boolean
					// If it's returned as a number (MySQL may return 0/1), convert to boolean
					if (product.is_visible_in_storefront === 0) {
						product.is_visible_in_storefront = false;
					} else if (product.is_visible_in_storefront === 1) {
						product.is_visible_in_storefront = true;
					} else if (
						product.is_visible_in_storefront === null ||
						product.is_visible_in_storefront === undefined
					) {
						// Default to true if not specified
						product.is_visible_in_storefront = true;
					}

					return product;
				});

				setFetchState('success');
				setProducts(processedProducts);
				ReactGA.event({
					category: 'Products',
					action: 'Fetched Products',
					label: 'Fetch Success',
				});
			})
			.catch((error) => {
				console.log('error fetching products', error);
				setFetchState('failed');
				ReactGA.event({
					category: 'Products',
					action: 'Fetch Products Failed',
					label: 'Fetch Failed',
				});
			});
	}, []);

	const handleRowClick = (params: { row: ProductInterface }) => {
		const product = params.row;
		ReactGA.event({
			category: 'User Interaction',
			action: 'Clicked Product Row',
			label: `Product ID: ${product.id}`,
		});
		setSelectedProduct(product);
		setIsEditPanelVisible(true);
	};

	const showCreateProductPanel = () => {
		ReactGA.event({
			category: 'User Interaction',
			action: 'Clicked Add New Product Button',
			label: 'Add New Product',
		});
		setSelectedProduct(null);
		setIsEditPanelVisible(true);
	};

	const updateProductInList = (updatedProduct: ProductInterface) => {
		setProducts(
			products.map((p: ProductInterface) => {
				if (p.id === updatedProduct.id) {
					return updatedProduct;
				}
				return p;
			})
		);
	};

	const removeProductFromList = (productId: string) => {
		setProducts(products.filter((p: ProductInterface) => p.id !== productId));
		ReactGA.event({
			category: 'Products',
			action: 'Deleted Product',
			label: `Product ID: ${productId}`,
		});
	};

	const hideCreateProductPanel = (product: ProductInterface | null = null) => {
		setIsEditPanelVisible(false);
		setSelectedProduct(null);

		if (product) {
			let isProductNew = true;

			setProducts(
				products.map((p: ProductInterface) => {
					if (p.id === product.id) {
						isProductNew = false;
						return product;
					}
					return p;
				})
			);

			if (isProductNew) {
				setProducts([...products, product]);
				ReactGA.event({
					category: 'Products',
					action: 'Created New Product',
					label: `Product ID: ${product.id}`,
				});
			} else {
				ReactGA.event({
					category: 'Products',
					action: 'Updated Product',
					label: `Product ID: ${product.id}`,
				});
			}
		} else {
			ReactGA.event({
				category: 'User Interaction',
				action: 'Closed Product Panel',
				label: 'No Product',
			});
		}
	};

	return (
		<Wrapper>
			<Header
				title="Products"
				description="Manage your products and categories"
				headerComponents={
					activeTab === 0
						? [
								<CreateButton key="create-product" onClick={showCreateProductPanel}>
									<ProductIcon />
									<span>Add Product</span>
								</CreateButton>,
						  ]
						: [
								<CreateButton
									key="create-category"
									onClick={() => {
										categoriesRef.current?.showCreatePanel();
									}}
								>
									<CategoryIcon style={{ fontSize: '18px' }} />
									<span>Add Category</span>
								</CreateButton>,
						  ]
				}
			/>
			<Tabbed
				style={{ marginTop: '10px' }}
				onChange={(index) => setActiveTab(index)}
				sections={[
					{
						label: 'Products',
						component: (
							<CustomTable
								columns={columns}
								objects={products}
								sx={{ marginTop: '0' }}
								onRowClick={(param: { row: ProductInterface }) => handleRowClick(param)}
								emptyStateComponent={() => (
									<EmptyState
										handleButtonClick={showCreateProductPanel}
										title="No Products Yet"
										description="Start by creating your first product. Once added, you can manage and track your products on the inventory page."
										buttonText="Add New Product"
										icon={<ProductIcon width={50} height={50} />}
									/>
								)}
								fetchState={fetchState}
								initialRowsPerPage={25}
							/>
						),
					},
					{
						label: 'Categories',
						component: <ProductCategories ref={categoriesRef} />,
					},
				]}
			/>
			<ProductModal
				isVisible={isEditPanelVisible}
				handleHidePanel={hideCreateProductPanel}
				product={selectedProduct}
				isNewProduct={selectedProduct === null}
				onProductUpdate={updateProductInList}
				onProductDelete={removeProductFromList}
			/>
		</Wrapper>
	);
}
